'use client';

import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { useLocale, useTranslations } from 'next-intl';
import { useActivateVoucher } from '@/hooks/useCartHooks';
import {
  isCurrentPromoActivatedAtom,
  isPromoExpiredAtom,
  promoConfigAtom,
} from '@/atoms/promoAtom';
import { getLocalizedValue } from '@/components/PromoPopup/PromoPopup';
import { AmplienceResponsiveImage } from '@/components/ResponsiveImage/AmplienceResponsiveImage';

const IMAGE_WIDTHS = [400, 500, 600, 800];

interface OnPageDiscountProps {
  className?: string;
}

export default function OnPageDiscount({ className }: Readonly<OnPageDiscountProps>) {
  const locale = useLocale();
  const t = useTranslations();
  const promoSlot = useAtomValue(promoConfigAtom);
  const isExpired = useAtomValue(isPromoExpiredAtom);
  const isActivated = useAtomValue(isCurrentPromoActivatedAtom);
  const { applyVoucher } = useActivateVoucher();

  if (!promoSlot || isExpired) {
    return null;
  }

  const content = promoSlot.content['promo-tool'];
  const title = getLocalizedValue(content.campaignDetails.title, locale);
  const description = getLocalizedValue(content.campaignDetails.description, locale);
  const voucherCode = content.campaignDetails.code;
  const backgroundImage = content.backgroundImage?.image ? content.backgroundImage : undefined;

  return (
    <div
      className={clsx(
        'mx-auto flex max-w-screen-xl flex-col flex-wrap justify-between gap-6 sm:flex-row md:gap-8 lg:gap-18 xl:gap-24.5',
        className
      )}
    >
      {backgroundImage && (
        <div className="sm:flex-1">
          <AmplienceResponsiveImage
            source={{
              src: backgroundImage.image,
              widths: IMAGE_WIDTHS,
              sizes: '(min-width: 768px) 40vw, 100vw',
            }}
            alt={backgroundImage.altText ?? ''}
          />
        </div>
      )}

      <div
        className={clsx(
          'flex flex-col justify-center gap-6 sm:flex-1',
          backgroundImage && 'md:pr-16'
        )}
      >
        <h3 className="text-3xl leading-tight font-black uppercase md:text-4xl">{title}</h3>

        <p className="text-sm leading-relaxed text-gray-800 md:text-base">{description}</p>

        {voucherCode && (
          <div className="flex flex-col items-start gap-3">
            <span className="text-sm text-gray-600">
              {t('promo.codeLabel')}: <span className="font-semibold">{voucherCode}</span>
            </span>

            {isActivated ? (
              <span className="rounded bg-green-100 px-4 py-2 text-green-700">
                {t('promo.codeActivated')}
              </span>
            ) : (
              <button
                type="button"
                onClick={applyVoucher}
                className="bg-black px-4 py-2 text-white hover:bg-gray-800"
              >
                {t('promo.applyButton.default')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
